import cookie from 'js-cookie';
import type { User } from 'types/User';

import type { View } from '~/routes/bolt/event/types';
import {
  getPageSeq,
  getPageViewId,
  getPreviousPageType,
  getPreviousPageViewId
} from '~/utils/navigationContext';
import type { BoltQueryString } from '~/utils/qs';
import { parseSearchString } from '~/utils/qs';

type EventData<T> = {
  clientIp: string | null;
  isBot: boolean;
  keepalive?: boolean;
  user: User;
  viewProperties?: T;
  viewType: string;
};

const SCHEMA_VERSION = 1;

const BRAZE_CHANNELS = [
  'banner',
  'content_card',
  'email',
  'in_app',
  'push'
] as const;

type BrazeChannel = (typeof BRAZE_CHANNELS)[number];

const BRAZE_UTM_SOURCE = 'braze';
const EMAIL_UTM_MEDIUM = 'email';

// article went to 2 when the module manifest became required, and to 3 when
// the manifest's ordinals became a dense count of the modules the viewport
// rendered: the number's meaning changed, and the version is how the
// warehouse tells the two eras apart on a row — never a rollout date.
const SCHEMA_VERSION_BY_VIEW_TYPE: Record<string, number> = {
  article: 3,
  plp: 2
};

class ViewEvent<T extends Record<string, unknown> = Record<string, unknown>> {
  private static lastSentId?: string;

  constructor(private data: EventData<T>) {}

  send() {
    if (this.data.isBot) return;

    const viewId = getPageViewId();

    if (!viewId || ViewEvent.lastSentId === viewId) return;

    ViewEvent.lastSentId = viewId;

    return fetch('/bolt/event', {
      body: JSON.stringify({ events: [this.buildEvent(viewId)] }),
      headers: { 'Content-Type': 'application/json' },
      keepalive: this.data.keepalive,
      method: 'POST'
    });
  }

  private schemaVersion(viewType: string): number {
    return SCHEMA_VERSION_BY_VIEW_TYPE[viewType] ?? SCHEMA_VERSION;
  }

  private normalizeViewType(pageType: string): string {
    if (pageType === 'homepage') return 'home';
    if (pageType === 'search') return 'plp';

    return pageType;
  }

  private firstScalar(value: unknown): string | undefined {
    if (Array.isArray(value)) return this.firstScalar(value[0]);

    return typeof value === 'string' && value.length > 0 ? value : undefined;
  }

  private brazeParam(value: unknown): string | undefined {
    const scalar = this.firstScalar(value);

    return scalar?.includes('{') ? undefined : scalar;
  }

  private linkBrazeChannel(value: unknown): BrazeChannel | undefined {
    const channel = this.firstScalar(value)?.toLowerCase();

    return BRAZE_CHANNELS.find((known) => known === channel);
  }

  private isBrazeEmail(params: BoltQueryString): boolean {
    return (
      this.firstScalar(params.utm_source)?.toLowerCase() === BRAZE_UTM_SOURCE &&
      this.firstScalar(params.utm_medium)?.toLowerCase() === EMAIL_UTM_MEDIUM
    );
  }

  private brazeChannel(params: BoltQueryString): BrazeChannel | undefined {
    const linkChannel = this.linkBrazeChannel(params.braze_channel);

    if (linkChannel) return linkChannel;

    return this.isBrazeEmail(params) ? 'email' : undefined;
  }

  private getBrazeParams(params: BoltQueryString) {
    return {
      braze_campaign_id: this.brazeParam(params.braze_campaign_id),
      braze_canvas_id: this.brazeParam(params.canvas_id),
      braze_canvas_message_variation_id: this.brazeParam(
        params.message_variation_id
      ),
      braze_canvas_step: this.brazeParam(params.canvas_step),
      braze_channel: this.brazeChannel(params)
    };
  }

  private getAcquisitionParams() {
    const params = parseSearchString(window.location.search);

    return {
      ...this.getBrazeParams(params),
      fbclid: this.firstScalar(params.fbclid),
      gclid: this.firstScalar(params.gclid),
      link_id: this.firstScalar(params.lid),
      msclkid: this.firstScalar(params.msclkid),
      ttclid: this.firstScalar(params.ttclid),
      utm_campaign: this.firstScalar(params.utm_campaign),
      utm_content: this.firstScalar(params.utm_content),
      utm_medium: this.firstScalar(params.utm_medium),
      utm_source: this.firstScalar(params.utm_source),
      utm_term: this.firstScalar(params.utm_term),
      wbraid: this.firstScalar(params.wbraid)
    };
  }

  private buildEvent(viewId: string): View<T> {
    const previousViewId = getPreviousPageViewId();
    const previousPageType = getPreviousPageType();
    const viewType = this.normalizeViewType(this.data.viewType);

    return {
      data: {
        ...this.getAcquisitionParams(),
        client_ts: Date.now(),
        guest_id: this.data.user.guestId ?? cookie.get('clickRefUid'),
        platform_properties: {
          client_ip: this.data.clientIp ?? undefined,
          page_path: window.location.pathname,
          referrer: document.referrer || undefined,
          user_agent: navigator.userAgent
        },
        previous_view_id: previousViewId,
        previous_view_type:
          previousViewId && previousPageType
            ? this.normalizeViewType(previousPageType)
            : undefined,
        schema_version: this.schemaVersion(viewType),
        view_id: viewId,
        view_properties: this.data.viewProperties,
        view_seq: getPageSeq(),
        view_type: viewType
      },
      type: 'view'
    };
  }
}

export default ViewEvent;
